import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Bot,
  Check,
  ChevronRight,
  Ellipsis,
  FlaskConical,
  Gauge,
  History,
  MessagesSquare,
  Minus,
  Pause,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  X,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  AUTOMATION_RUN_STATUS_LABEL,
  AUTOMATION_STEP_LABEL,
  TRIGGER_EVENTS,
  type Paginated,
} from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { WorkflowEditor, type Workflow } from "@/components/crm/WorkflowEditor";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { formatDateTime, relativeTime } from "@/lib/crm";
import { cn } from "@/lib/utils";

type ResultadoDaRevisao = {
  adicionadas: Array<{ label: string; points: number }>;
  desativadas: Array<{ label: string; points: number }>;
  jaEstavamOk: number;
};

/**
 * Revisão da pontuação contra as regras recomendadas.
 *
 * As regras são copiadas do modelo quando a empresa é criada e nunca mais — o
 * que faz sentido, porque cada clínica ajusta as suas. O problema é que uma
 * correção no modelo não alcança quem já existe, e a correção desta vez muda
 * resultado de verdade: havia uma regra tirando pontos do lead porque NÓS
 * demoramos a responder, o que zerava a pontuação de quem tinha escrito.
 *
 * Fica aqui, e não escondido em Configurações, porque quem cuida de automação
 * é quem entende a consequência de mexer nisso.
 */
function RevisaoDaPontuacao() {
  const [resultado, setResultado] = useState<ResultadoDaRevisao | null>(null);

  const revisar = useMutation({
    mutationFn: () => api.post<ResultadoDaRevisao>("/automations/scoring/review"),
    onSuccess: (r) => {
      setResultado(r);
      const mudou = r.adicionadas.length + r.desativadas.length;
      toast.success(
        mudou === 0 ? "A pontuação já estava em dia" : `${mudou} ajuste(s) aplicado(s)`,
      );
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível revisar"),
  });

  return (
    <Card className="border-primary/30">
      <CardContent className="flex flex-wrap items-start gap-4 pt-5">
        <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Gauge className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-semibold">Pontuação e temperatura do lead</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Acrescenta as regras que leem a intenção da mensagem — quem pergunta preço ou quer
            agendar deixa de valer o mesmo que um &quot;bom dia&quot; — e desliga a regra que
            descontava pontos do lead por demora nossa. Não mexe no que você ajustou.
          </p>
        </div>
        <Button
          variant="soft"
          size="sm"
          disabled={revisar.isPending}
          onClick={() => revisar.mutate()}
        >
          {revisar.isPending ? "Revisando…" : "Revisar regras"}
        </Button>

        {resultado && (
          <div className="w-full space-y-1 border-t pt-3 text-xs text-muted-foreground">
            {resultado.adicionadas.length > 0 && (
              <p>
                <strong className="font-medium text-foreground">Acrescentadas:</strong>{" "}
                {resultado.adicionadas
                  .map((r) => `${r.label} (${r.points > 0 ? "+" : ""}${r.points})`)
                  .join(", ")}
              </p>
            )}
            {resultado.desativadas.length > 0 && (
              <p>
                <strong className="font-medium text-foreground">Desligadas:</strong>{" "}
                {resultado.desativadas.map((r) => `${r.label} (${r.points})`).join(", ")}
              </p>
            )}
            {resultado.adicionadas.length === 0 && resultado.desativadas.length === 0 && (
              <p>Nada a mudar: as {resultado.jaEstavamOk} regras já estão como o recomendado.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const Route = createFileRoute("/_authenticated/automacao")({
  component: AutomacaoPage,
});

type StepState = {
  index: number;
  type: keyof typeof AUTOMATION_STEP_LABEL;
  status: "done" | "skipped" | "failed";
  detail?: string;
  at: string;
};

type Run = {
  id: string;
  status: keyof typeof AUTOMATION_RUN_STATUS_LABEL;
  workflowId: string;
  workflowName: string;
  workflowVersion: number;
  leadId: string | null;
  currentStepIndex: number;
  stepStates: StepState[];
  resumeAt: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

const gatilhoLabel = (value: string) =>
  TRIGGER_EVENTS.find((t) => t.value === value)?.label ?? value;

/** Situação da execução → etiqueta do tema. */
const TOM_DA_EXECUCAO: Record<string, NonNullable<BadgeProps["variant"]>> = {
  completed: "success",
  running: "info",
  waiting: "warning",
  failed: "danger",
  pending: "muted",
};

/**
 * Automação.
 *
 * Duas telas, não uma: a lista responde "o que está ligado" e as execuções
 * respondem "por que o cliente recebeu aquilo". A segunda pergunta é a que
 * aparece às sete da noite, e ela precisa ter resposta sem abrir o banco.
 */
function AutomacaoPage() {
  const queryClient = useQueryClient();
  const [editando, setEditando] = useState<Workflow | "novo" | null>(null);

  const workflows = useQuery({
    queryKey: ["automations"],
    queryFn: () => api.get<Workflow[]>("/automations"),
  });

  const runs = useQuery({
    queryKey: ["automation-runs"],
    queryFn: () => api.get<Paginated<Run>>("/automation-runs", { pageSize: 50 }),
    // As execuções mudam sozinhas: quem abre esta tela está esperando ver algo
    // acontecer, e um F5 manual para descobrir seria o oposto do que ela serve.
    refetchInterval: 10_000,
  });

  const alternar = useMutation({
    mutationFn: (w: Workflow) => api.patch(`/automations/${w.id}`, { active: !w.active }),
    onSuccess: (_d, w) => {
      void queryClient.invalidateQueries({ queryKey: ["automations"] });
      toast.success(w.active ? "Automação pausada" : "Automação ativada");
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível alterar"),
  });

  const testar = useMutation({
    mutationFn: (w: Workflow) =>
      api.post<{ wouldTrigger: boolean; reason: string | null; plannedSteps: unknown[] }>(
        `/automations/${w.id}/test`,
        { payload: {} },
      ),
    onSuccess: (r) =>
      toast.info(
        r.wouldTrigger
          ? `Dispararia e rodaria ${r.plannedSteps.length} passo(s)`
          : `Não dispararia: ${r.reason}`,
      ),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível simular"),
  });

  const reprocessar = useMutation({
    mutationFn: (run: Run) => api.post(`/automation-runs/${run.id}/retry`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["automation-runs"] });
      toast.success("Execução recolocada na fila");
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Não foi possível reprocessar"),
  });

  const ligados = (workflows.data ?? []).filter((w) => w.active).length;

  return (
    <>
      <PageHeader
        title="Automação"
        description={
          workflows.data
            ? `${ligados} de ${workflows.data.length} automação(ões) ativa(s)`
            : "O que acontece sozinho"
        }
        action={
          <Button onClick={() => setEditando("novo")}>
            <Plus />
            Nova automação
          </Button>
        }
      />

      <PageBody className="space-y-5">
        {/* O Typechat mora em tela própria (editor de grafo), mas nasce daqui:
            uma regra pode iniciá-lo com o passo "Iniciar Typechat". */}
        <Link
          to="/typechat"
          className="group flex items-center gap-3 rounded-xl border bg-card px-4 py-3 shadow-card transition-[box-shadow,transform] duration-150 hover:-translate-y-px hover:shadow-panel focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <MessagesSquare className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">Typechat</span>
            <span className="block text-xs text-muted-foreground">
              Fluxos de conversa no WhatsApp e no site
            </span>
          </span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
        </Link>

        <RevisaoDaPontuacao />

        <Tabs defaultValue="workflows">
          <TabsList>
            <TabsTrigger value="workflows">Automações</TabsTrigger>
            <TabsTrigger value="execucoes">Execuções</TabsTrigger>
          </TabsList>

          <TabsContent value="workflows">
            {workflows.isPending ? (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 3 }, (_, i) => (
                  <Skeleton key={i} className="h-44 w-full rounded-xl" />
                ))}
              </div>
            ) : workflows.data?.length === 0 ? (
              <EmptyState
                icon={Bot}
                title="Nenhuma automação ainda"
                description="Crie a primeira: um follow-up para quem não respondeu é o que mais devolve lead."
                action={
                  <Button variant="outline" size="sm" onClick={() => setEditando("novo")}>
                    <Plus />
                    Nova automação
                  </Button>
                }
                className="rounded-xl border border-dashed"
              />
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {workflows.data?.map((w) => (
                  <Card key={w.id} className="flex flex-col">
                    <CardHeader className="flex-nowrap">
                      <div className="min-w-0 flex-1">
                        <CardTitle className="truncate" title={w.name}>
                          {w.name}
                        </CardTitle>
                        {w.description && (
                          <CardDescription className="line-clamp-2">
                            {w.description}
                          </CardDescription>
                        )}
                      </div>
                      <Badge variant={w.active ? "success" : "muted"}>
                        {w.active ? "Ativa" : "Pausada"}
                      </Badge>
                    </CardHeader>
                    <CardContent className="flex-1 space-y-2.5 pt-3">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="muted" className="max-w-full">
                          <Zap />
                          <span className="truncate">{gatilhoLabel(w.triggerEvent)}</span>
                        </Badge>
                        <Badge variant="outline">v{w.version}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {w.steps?.length ?? 0} passo(s)
                        {w.cooldownSeconds
                          ? ` · intervalo de ${Math.round(w.cooldownSeconds / 60)} min`
                          : " · sem intervalo mínimo"}
                      </p>
                    </CardContent>
                    <CardFooter className="justify-between">
                      <span className="tabular-nums">
                        {typeof w.runCount === "number" && w.runCount > 0
                          ? `${w.runCount} execução(ões)`
                          : "nunca executou"}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          size="xs"
                          variant={w.active ? "outline" : "soft"}
                          disabled={alternar.isPending}
                          onClick={() => alternar.mutate(w)}
                        >
                          {w.active ? <Pause /> : <Play />}
                          {w.active ? "Pausar" : "Ativar"}
                        </Button>
                        <DropdownMenu>
                          <Hint label="Mais ações">
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="icon-xs"
                                variant="ghost"
                                aria-label={`Ações de ${w.name}`}
                                className="text-muted-foreground"
                              >
                                <Ellipsis />
                              </Button>
                            </DropdownMenuTrigger>
                          </Hint>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setEditando(w)}>
                              <Pencil />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => testar.mutate(w)}>
                              <FlaskConical />
                              Simular disparo
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="execucoes">
            <Card>
              <CardHeader>
                <div className="min-w-0">
                  <CardTitle>Execuções</CardTitle>
                  <CardDescription>
                    As 50 mais recentes, atualizadas a cada 10 segundos. Cada passo mostra se rodou,
                    foi pulado ou falhou.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0 pt-3">
                {runs.isPending ? (
                  <div className="space-y-2 px-5 pb-5">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : runs.data?.data.length === 0 ? (
                  <EmptyState
                    compact
                    icon={History}
                    title="Nenhuma execução ainda"
                    description="Elas aparecem aqui assim que um gatilho acontecer."
                    className="pb-8"
                  />
                ) : (
                  <div className="overflow-x-auto rounded-b-xl">
                    <table className="w-full min-w-[52rem] text-sm">
                      <thead className="bg-muted/60 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <tr>
                          <th className="px-5 py-2.5 text-left">Situação</th>
                          <th className="px-4 py-2.5 text-left">Automação</th>
                          <th className="px-4 py-2.5 text-left">Quando</th>
                          <th className="px-4 py-2.5 text-left">Passos</th>
                          <th className="px-5 py-2.5" />
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {runs.data?.data.map((run) => (
                          <tr
                            key={run.id}
                            className="align-top transition-colors hover:bg-muted/40"
                          >
                            <td className="px-5 py-2.5">
                              <Badge variant={TOM_DA_EXECUCAO[run.status] ?? "muted"}>
                                {AUTOMATION_RUN_STATUS_LABEL[run.status]}
                              </Badge>
                              {run.resumeAt && (
                                <p className="mt-1 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
                                  retoma {formatDateTime(run.resumeAt)}
                                </p>
                              )}
                            </td>
                            <td className="px-4 py-2.5">
                              <p
                                className="max-w-[16rem] truncate font-medium"
                                title={run.workflowName}
                              >
                                {run.workflowName}
                              </p>
                              <p className="text-[11px] text-muted-foreground">
                                v{run.workflowVersion}
                              </p>
                            </td>
                            <td
                              className="whitespace-nowrap px-4 py-2.5 text-xs tabular-nums text-muted-foreground"
                              title={formatDateTime(run.createdAt)}
                            >
                              {relativeTime(run.createdAt)}
                            </td>
                            <td className="px-4 py-2.5">
                              {run.stepStates?.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {run.stepStates.map((s) => (
                                    <PassoExecutado key={`${run.id}-${s.index}`} passo={s} />
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                              {run.error && (
                                <p className="mt-1.5 max-w-md text-xs text-destructive">
                                  {run.error}
                                </p>
                              )}
                            </td>
                            <td className="px-5 py-2.5 text-right">
                              {run.status === "failed" && (
                                <Button
                                  size="xs"
                                  variant="outline"
                                  disabled={reprocessar.isPending}
                                  onClick={() => reprocessar.mutate(run)}
                                >
                                  <RotateCcw />
                                  Reprocessar
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </PageBody>

      {/* A `key` remonta o editor a cada automação aberta: o estado inicial
          dele é lido do `workflow` na montagem, e sem isso editar a segunda
          abriria com os campos da primeira. */}
      <WorkflowEditor
        key={editando === null ? "fechado" : editando === "novo" ? "novo" : editando.id}
        workflow={editando}
        onOpenChange={(open) => !open && setEditando(null)}
      />
    </>
  );
}

/** Um passo da execução como chip: verde rodou, cinza pulou, vermelho falhou. */
function PassoExecutado({ passo }: { passo: StepState }) {
  const Icone = passo.status === "failed" ? X : passo.status === "skipped" ? Minus : Check;
  return (
    <span
      title={passo.detail}
      className={cn(
        "inline-flex max-w-[14rem] items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
        passo.status === "failed"
          ? "bg-destructive/10 text-destructive"
          : passo.status === "skipped"
            ? "bg-muted text-muted-foreground"
            : "bg-success/12 text-success",
      )}
    >
      <Icone className="size-3 shrink-0" />
      <span className="truncate">{AUTOMATION_STEP_LABEL[passo.type] ?? passo.type}</span>
    </span>
  );
}
